<template>
    <div class="flow-graph" :class="componentClasses">
        <top-bar
            class="flow-graph__top-bar"
            :flow-active="flow.active"
            :flow-name="flow.name"
            :brand="flow.brand"
            :teams="flow.teams"
            @flow-active-changed="onFlowActiveChanged"
            @flow-name-changed="onFlowNameChanged"
        />

        <div class="sidepanel">
            <component
                :is="editingNodeSettingsComponent"
                v-if="editingNode"
                v-model:auto-delete-enabled="flow.autoDeleteEnabled"
                v-model:auto-delete-after-quantity="flow.autoDeleteAfterQuantity"
                v-model:auto-delete-after-unit="flow.autoDeleteAfterUnit"
                :node="editingNode"
                :flow="flow"
                @save-node="saveNode"
            />
        </div>

        <vue-flow-component
            v-model="flowData"
            class="flow-graph__content"
            fit-view-on-init
            :node-types="customTypeNodeMap"
            :min-zoom="0.5"
            :max-zoom="1"
            :nodes-draggable="false"
            @click.capture="editingNode = null"
        >
            <background-component
                :gap="32"
                :size="8"
                pattern-color="#f2f2f2"
            />
            <controls-component
                :show-interactive="false"
            />
        </vue-flow-component>
    </div>
</template>

<script setup lang="ts">
    import { computed, provide, type Ref, ref } from 'vue';
    import { Background as BackgroundComponent } from '@vue-flow/background';
    import { Controls as ControlsComponent } from '@vue-flow/controls';
    import { customTypeNodeMap, customTypeSettingsMap, getCustomTypeName } from '@/flow/flow/utils/custom-types';
    import { ChangeFlowCommand, ChangeFlowNameCommand, Flow, type Node, type Trigger } from '@keyvue/one-api-client';
    import { useVueFlow, VueFlow as VueFlowComponent } from '@vue-flow/core';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { ActivateFlowCommand, DeactivateFlowCommand } from '@keyvue/one-api-client';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import useGraphGenerator from '@/flow/flow/composables/useGraphGenerator';
    import TopBar from '@/flow/flow/components/graph/TopBar.vue';

    const props = defineProps<{ flowId: string }>();
    const { client } = useClient();
    const flow = ref(await client.get(Flow, props.flowId)) as Ref<Flow>;
    const { generateFlowData } = useGraphGenerator();
    const { notifySaving, notifySaved, notifyError } = useNotify();

    const flowData = computed(() => generateFlowData(flow.value));
    const editingNode = ref<Node | Trigger | null>(null);

    const findNode = (id: string) => {
        return [...flow.value.triggers, ...flow.value.nodes].find((node) => {
            return node.id.value === id;
        });
    };

    const { onNodeClick } = useVueFlow();
    onNodeClick((event) => {
        const node = findNode(event.node.id);

        if (!node) {
            return;
        }

        editingNode.value = node as Node;
    });

    /**
     * Find the settings component matching the currently selected node.
     */
    const editingNodeSettingsComponent = computed(() => {
        if (editingNode.value === null) {
            return null;
        }

        const customTypeName = getCustomTypeName(editingNode.value as Node | Trigger);
        return customTypeSettingsMap[customTypeName];
    });

    const componentClasses = computed(() => {
        return {
            'flow-graph--show-sidepanel': !!editingNode.value,
        };
    });

    function stopEditingNode() {
        editingNode.value = null;
    }

    function applyOptions(options: any) {
        if (editingNode.value) {
            editingNode.value.options = options;
        }
    }

    async function saveNode(options: any, stopEditing: boolean = false, message: string = 'De flow is opgeslagen') {
        try {
            const closeSavingNotification = notifySaving(undefined, 0);
            applyOptions(options);
            await saveFlow(stopEditing);

            if (stopEditing) {
                stopEditingNode();
            }

            closeSavingNotification();
            notifySaved(undefined, message);
        } catch (error) {
            notifyError(error);
        }
    }

    async function saveFlow(stopEditing: boolean = true) {
        await client.dispatch(
            ChangeFlowCommand.fromFlow(flow.value),
        );

        await reloadFlow();

        if (stopEditing) {
            stopEditingNode();
        }
    }

    async function reloadFlow() {
        flow.value = await client.get(Flow, props.flowId);
    }

    async function onFlowActiveChanged(value: boolean) {
        const handler = value ? activateFlow : deactivateFlow;
        await handler();
    }

    async function onFlowNameChanged(value: string) {
        const currentValue = flow.value.name;

        try {
            flow.value.name = value;
            await client.dispatch(ChangeFlowNameCommand.fromFlow(flow.value));
            notifySaved('De naam van deze flow is aangepast', '');
        } catch (error) {
            flow.value.name = currentValue;
            notifyError(error);
        }
    }

    async function activateFlow() {
        const currentValue = flow.value.active;

        try {
            flow.value.active = true;
            await client.dispatch(ActivateFlowCommand.fromFlow(flow.value));
            notifySaved('De flow is geactiveerd', '');
        } catch (error) {
            flow.value.active = currentValue;
            notifyError(error);
        }
    }

    async function deactivateFlow() {
        const currentValue = flow.value.active;

        try {
            flow.value.active = false;
            await client.dispatch(DeactivateFlowCommand.fromFlow(flow.value));
            notifySaved('De flow is gedeactiveerd', '');
        } catch (error) {
            flow.value.active = currentValue;
            notifyError(error);
        }
    }

    provide('applyOptions', applyOptions);
    provide('stopEditingNode', stopEditingNode);
    provide('saveFlow', saveFlow);
</script>

<style>
/* @todo: verplaatsen naar main.ts? */
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
@import '@vue-flow/controls/dist/style.css';
</style>

<style scoped lang="scss">
.flow-graph {
    display: grid;
    grid-template-columns: 0 1fr;
    grid-template-rows: min-content 1fr;
    transition: grid .2s ease-in-out;
    will-change: grid;
    width: 100%;
    height: 100%;

    &--show-sidepanel {
        grid-template-columns: 450px 1fr;
    }

    &__top-bar {
        grid-column: span 2;
    }

    .sidepanel {
        grid-row: span 2;
        background-color: white;
        border-right: solid 3px color(gray-300);
        width: 449px;
    }

    &__content {
        background-color: orangered;
    }
}

.vue-flow__background {
    background-color: #f8f8f8;
}

:deep(.vue-flow__edge-text) {
    font-size: size(md);
    fill: color(gray-800);
}

:deep(.vue-flow__handle) {
    opacity: 0;
    pointer-events: none;
}

:deep(.vue-flow__edge-path) {
    stroke: color(gray-300);
    stroke-width: 2;
}

:deep(.vue-flow__edge-textbg) {
    fill: #f8f8f8;
}

.vue-flow__controls {
    bottom: unset;
    left: unset;
    top: size(lg);
    right: size(lg);
    background-color: white;
    overflow: hidden;
    border-radius: size(sm);
    transform: scale(1.25);
}
</style>
